<template>
  <BaseModal :modalTitle="$t('W: Opret tekstblok')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <UiInputTextFieldVee
        name="title"
        :label="$t('W: Titel')"
        :rules="['required']"
        isRequired
        showAsRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.title" />

      <div>
        <span class="mb-1.5 block text-sm font-medium text-text-secondary">
          {{ $t('term.text') }}
        </span>
        <RichTextEditor v-model="form.text" />
      </div>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useAppStore } from '@/stores/appStore'
import { useToast } from '@/globals/typescript/utils/toast'
import { sanitizeRichText } from '@/globals/typescript/utils/sanitizeRichText'
import { mapActions, mapState } from 'pinia'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

function createForm() {
  return {
    title: '',
    text: '',
  }
}

export default {
  name: 'CreateReportDescriptionItemModal',

  emits: ['close'],

  props: {
    reportDescriptionID: { type: String as PropType<string>, required: true },
  },

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useAppStore, ['currentLocale']),
  },

  methods: {
    ...mapActions(useReportDescriptionItemStore, [
      'createReportDescriptionItem',
    ]),

    async onSubmit(): Promise<void> {
      this.isLoading = true
      this.errorText = ''
      try {
        await this.createReportDescriptionItem({
          reportDescriptionID: this.reportDescriptionID,
          title: this.form.title,
          text: sanitizeRichText(this.form.text),
        })
        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Tekstblok oprettet'))
        this.$emit('close')
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette tekstblok'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
